import type { ReactNode } from 'react';
import { useCallback, useRef } from 'react';

import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import type {
  OnboardingHighlightConfig,
  OnboardingHighlightName,
} from '#src/contexts/onboardingHighlight/index.tsx';
import { useOnboardingHighLightContext } from '#src/contexts/onboardingHighlight/index.tsx';
import { useResizeObserver } from '#src/hooks/ui/useResizeObserver.ts';

type WrapperProps = {
  highlightName: OnboardingHighlightName;
  children: ReactNode;
  config?: OnboardingHighlightConfig;
  className?: string;
};
const Wrapper = ({
  highlightName,
  children,
  config,
  className,
}: WrapperProps) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const { registerHighlight } = useOnboardingHighLightContext();

  const registerHighlightCb = useCallback(() => {
    registerHighlight({
      name: highlightName,
      measureCallback: () => {
        if (ref.current) {
          return ref.current.getBoundingClientRect();
        }
        return null;
      },
      config,
      children,
      refToScroll: ref,
    });
  }, [children, config, highlightName, registerHighlight]);

  useResizeObserver(ref, () => {
    registerHighlightCb();
  });

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
};

type Props = {
  highlightName: OnboardingHighlightName;
  children: ReactNode;
  config?: OnboardingHighlightConfig;
  wrap?: boolean;
  className?: string;
};
export const OnboardingHighlightWrapper = ({
  children,
  highlightName,
  config,
  wrap = true,
  className,
}: Props): ReactNode => {
  const { currentUser } = useCurrentUserContext();
  const { activeHighlights } = useOnboardingHighLightContext() || {
    activeHighlights: [],
  };
  const active = activeHighlights?.includes(highlightName);

  if (currentUser && active && wrap) {
    return (
      <Wrapper
        highlightName={highlightName}
        config={config}
        className={className}
      >
        {children}
      </Wrapper>
    );
  }

  return children;
};
